import { Inject, Injectable } from '@angular/core';
import { map, shareReplay, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { CrmObjectService } from '../../../../shared-services';
import { GetMultiCrmObjectResponse } from '@vendasta/crm';
import { ActivatedRoute, Params } from '@angular/router';
import { PartnerService } from '@galaxy/partner';
import { AuxiliaryFieldSchemaApiService, ListAuxiliaryDataFieldSchemaResponse } from '@vendasta/account-group';
import { CrmDependencies, CrmInjectionToken } from '../../../../tokens-and-interfaces';

@Injectable()
export class SyncFieldFormInputService {
  private crmObjectId$: Observable<string> = this.route.params.pipe(
    map((params: Params) => params['crmObjectId']),
    distinctUntilChanged(),
    shareReplay(1),
  );
  private crmObject$: Observable<GetMultiCrmObjectResponse> = this.crmObjectId$.pipe(
    switchMap(
      (crmObjectId: string) =>
        this.crmObjectService.getMultiObject('Company', [crmObjectId]) as Observable<GetMultiCrmObjectResponse>,
    ),
    shareReplay(1),
  );

  private customFieldIds$: Observable<string[]> = this.partnerService.getPartnerId().pipe(
    switchMap((partnerId: string) => {
      return this.schemaService.listAuxiliaryDataFieldSchema({
        partnerId: partnerId,
        pagingOptions: {
          pageSize: 100,
          cursor: '',
        },
      });
    }),
    map((response: ListAuxiliaryDataFieldSchemaResponse) => {
      if (!response || !response.fieldSchemas) return [];
      return response.fieldSchemas.map((field) => field.fieldId);
    }),
  );

  private syncFieldIds$ = combineLatest([this.config.company?.syncIds$ ?? of([]), this.customFieldIds$]).pipe(
    map(([syncIds, customFieldIds]) => {
      return [...syncIds, ...customFieldIds];
    }),
    shareReplay(1),
  );

  constructor(
    private readonly crmObjectService: CrmObjectService,
    private readonly route: ActivatedRoute,
    private readonly schemaService: AuxiliaryFieldSchemaApiService,
    private readonly partnerService: PartnerService,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
  ) {}

  getAccountId(): Observable<string> {
    return this.config.company?.accountId$?.(this.crmObject$) ?? of('');
  }

  hasAccount(): Observable<boolean> {
    return this.getAccountId().pipe(map((id) => !!id));
  }

  getSyncFieldIds(): Observable<string[]> {
    return this.syncFieldIds$;
  }
}
